import React, { useEffect, useMemo, useRef, useState } from 'react'

import {
  Box,
  Container,
  InputAdornment,
  TextField,
  Typography,
} from '@material-ui/core'
import { Search } from '@material-ui/icons'
import { isEmpty } from 'lodash'

import { LoadingFeedback } from 'components'
import CategoriesSection from './components/CategoriesSection'
import ChipsFilter from './components/ChipsFilter'
import CoursesRow from './components/CoursesRow'

import useAuthTraining from 'hooks/useAuthTraining'
import useSnackbar from 'hooks/useSnackbar'
import helpers from 'helpers'
import * as service from 'service'
import useStyles from './styles'

const ALL_CHIP_ID = 'all'
const OTHERS_CHIP_ID = 'others'

const Home = ({ match }) => {
  const classes = useStyles()
  const snackbar = useSnackbar()
  const { company, portalToken: contextPortalToken } = useAuthTraining()

  const portalToken = match?.params?.portalToken || contextPortalToken

  const [allCourses, setAllCourses] = useState([])
  const [courses, setCourses] = useState([])
  const [inProgressCourses, setInProgressCourses] = useState([])
  const [categories, setCategories] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [activeChip, setActiveChip] = useState(ALL_CHIP_ID)
  const isFirstRender = useRef(true)

  const activeCategoryId =
    activeChip === ALL_CHIP_ID || activeChip === OTHERS_CHIP_ID
      ? null
      : Number(activeChip)

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      try {
        const [coursesResponse, categoriesResponse, inProgressResponse] =
          await Promise.all([
            service.trainingPortal.courses.index({ perPage: 10000 }),
            service.trainingPortal.courses.categories(),
            service.trainingPortal.courses.inProgress(),
          ])
        const loadedCourses = coursesResponse?.data?.courses || []
        setAllCourses(loadedCourses)
        setCourses(loadedCourses)
        setCategories(categoriesResponse?.data?.categories || [])
        setInProgressCourses(inProgressResponse?.data?.courses || [])
      } catch (error) {
        snackbar.open({
          message:
            helpers.formatters.errorMessage(error?.response?.data?.error) ||
            'Erro ao carregar os treinamentos.',
          variant: 'error',
        })
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400)
    return () => clearTimeout(timer)
  }, [searchTerm])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    const fetchFiltered = async () => {
      try {
        const filterParams = {}
        if (debouncedSearch.trim()) filterParams.q = debouncedSearch.trim()
        if (activeCategoryId) filterParams.category_id = activeCategoryId

        const [coursesResponse, inProgressResponse] = await Promise.all([
          service.trainingPortal.courses.index(filterParams),
          service.trainingPortal.courses.inProgress(filterParams),
        ])
        setCourses(coursesResponse?.data?.courses || [])
        setInProgressCourses(inProgressResponse?.data?.courses || [])
      } catch (error) {
        snackbar.open({
          message:
            helpers.formatters.errorMessage(error?.response?.data?.error) ||
            'Erro ao filtrar os treinamentos.',
          variant: 'error',
        })
      }
    }

    fetchFiltered()
    // eslint-disable-next-line
  }, [debouncedSearch, activeCategoryId])

  const categoriesByCount = useMemo(
    () =>
      [...categories]
        .map((category) => ({
          ...category,
          courseCount: allCourses.filter((course) =>
            course.categories?.some((c) => c.id === category.id),
          ).length,
        }))
        .sort((a, b) => b.courseCount - a.courseCount),
    [categories, allCourses],
  )

  const chips = useMemo(
    () => [
      { id: ALL_CHIP_ID, label: 'Todos' },
      ...categoriesByCount.slice(0, 3).map((category) => ({
        id: String(category.id),
        label: category.name,
      })),
      ...(categoriesByCount.length > 3
        ? [{ id: OTHERS_CHIP_ID, label: 'Outros' }]
        : []),
    ],
    [categoriesByCount],
  )

  const otherCategories = useMemo(
    () =>
      categoriesByCount.slice(3).map((category) => ({
        id: String(category.id),
        label: category.name,
      })),
    [categoriesByCount],
  )

  const categoriesSectionRef = useRef(null)

  const handleClearSearch = () => {
    setSearchTerm('')
    setDebouncedSearch('')
    setActiveChip(ALL_CHIP_ID)
  }

  if (isLoading) return <LoadingFeedback open />

  return (
    <Container maxWidth="lg">
      <Box className={classes.hero}>
        <Typography className={classes.heroTitle}>
          O que você quer aprender hoje?
        </Typography>
        <Typography className={classes.heroSubtitle}>
          Explore nossa biblioteca de cursos e treinamentos. Desenvolva novas
          habilidades e mantenha suas certificações sempre em dia.
        </Typography>
        <Box className={classes.searchRow}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Buscar cursos..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className={classes.searchField}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {!isEmpty(chips) && (
          <ChipsFilter
            chips={chips}
            activeChip={activeChip}
            onChipChange={setActiveChip}
            otherCategories={otherCategories}
          />
        )}
      </Box>
      {!isEmpty(inProgressCourses) && (
        <CoursesRow
          title="Em andamento"
          courses={inProgressCourses}
          hasAnyEnrolled
          portalToken={portalToken}
        />
      )}

      <CoursesRow
        courses={courses}
        hasAnyEnrolled={!isEmpty(allCourses)}
        companyName={company?.name}
        portalToken={portalToken}
        searchTerm={searchTerm}
        onClearSearch={handleClearSearch}
      />

      {!isEmpty(categories) && !isEmpty(allCourses) && (
        <div ref={categoriesSectionRef}>
          <CategoriesSection
            categories={categories}
            allCourses={allCourses}
            portalToken={portalToken}
          />
        </div>
      )}
    </Container>
  )
}

export default Home
